import {
  CircleAlert,
  Edit3Icon,
  MessagesSquare,
  MoreVerticalIcon,
  PanelLeft,
  SquarePen,
  StarIcon,
  TrashIcon,
  UsersIcon,
} from "lucide-react";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  Suspense,
  Component,
  ErrorInfo,
  ReactNode,
} from "react";
import { useMutation, useQuery, useSuspenseQuery } from "@apollo/client/react";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import { gql } from "graphql-tag";
import { rehypeLinkifySources } from "../lib/rehype-plugins";
import { setLytraSentryContext } from "../lib/sentryContext";
import { AssistantChatInput } from "./AssistantChatInput";
import { AssistantMessageFeed, ownFeedback } from "./AssistantMessageFeed";
import { useAssistantMessages } from "../hooks/useAssistantMessages";
import { useAnswerReviews } from "../hooks/useAnswerReviews";
import { AnswerReviews, chatRating, rated } from "./AnswerReview";
import type { ChatFeedback, ChatRating } from "./v2/ChatMessage";
import type { AssistantMessage, AssistantReference } from "../types/assistant";
import { ReferenceType } from "../types/assistant";
import type { PresignedUrlEntry } from "../hooks/usePresignedDocumentUrls";
import { AiMarker } from "./v2/ui/ai-marker";
import { Button } from "./v2/ui/button";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./v2/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./v2/ui/dropdown-menu";
import { EmptyState } from "./v2/ui/empty-state";
import { Field } from "./v2/ui/field";
import { Icon } from "./v2/ui/icon";
import { IconButton } from "./v2/ui/icon-button";
import { Input } from "./v2/ui/input";
import {
  List,
  LIST_ROW_ACTIONS_REVEAL,
  LIST_ROW_HOVER,
  LIST_ROW_SELECTED,
  LIST_ROW_SELECTED_TEXT,
} from "./v2/ui/list-item";
import { FOCUS_RING_INSET_AFTER } from "./v2/lib/focus";
import { Pane, PaneBody, PaneHeader } from "./v2/ui/page-layout";
import { Skeleton } from "./v2/ui/skeleton";
import { Throbber } from "./v2/ui/throbber";
import { formatDate } from "./v2/lib/date";

// Type definitions for GraphQL responses (Chat-specific)
interface AssistantConversationListItem {
  id: string;
  createdAt: string;
  name?: string | null;
  isFavorite: boolean;
  isShared: boolean;
  userId: string;
  messages: {
    edges: { node: { id: string; text: string; createdAt?: string | null } }[];
  };
}

interface AssistantConversationsQueryData {
  assistantConversations: AssistantConversationListItem[];
}

interface AssistantConversationStartMutationData {
  assistantConversationStart: {
    id: string;
  };
}

const DOCUMENT_LINKS_QUERY = gql(`
  query CustomerChatDocumentLinks($documentIds: [UUID!]!) {
    documentLinks(documentIds: $documentIds) {
      documentId
      sourceUrl
    }
  }
`);

interface DocumentLinksQueryData {
  documentLinks: { documentId: string; sourceUrl: string }[];
}

// A document's public original never expires, unlike our signed file links.
const NO_EXPIRY = Number.MAX_SAFE_INTEGER;

interface DocumentUrlMutationData {
  documentUrl: {
    id: string;
    filename: string;
    presignedUrl: string;
  };
}

// Error Boundary for graceful error handling
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

const ASSISTANT_CONVERSATIONS_QUERY = gql(`
  query AssistantConversations {
    assistantConversations {
      id
      createdAt
      name
      isFavorite
      isShared
      userId
      messages {
        edges {
          node {
            id
            text
            createdAt
          }
        }
      }
    }
  }
`);

const CREATE_CONVERSATION_MUTATION = gql(`
  mutation StartChatConversation(
    $text: String!
    $researchMode: Boolean
    $attachmentIds: [UUID!]
  ) {
    assistantConversationStart(
      text: $text
      researchMode: $researchMode
      attachmentIds: $attachmentIds
    ) {
      id
    }
  }
`);

const SET_RESEARCH_MODE_MUTATION = gql(`
  mutation SetChatResearchMode($conversationId: UUID!, $enabled: Boolean!) {
    assistantConversationSetResearchMode(
      conversationId: $conversationId
      enabled: $enabled
    ) {
      id
      researchMode
    }
  }
`);

const DELETE_CONVERSATION_MUTATION = gql(`
  mutation DeleteAssistantConversation($conversationId: UUID!) {
    assistantConversationDelete(conversationId: $conversationId)
  }
`);

const RENAME_CONVERSATION_MUTATION = gql(`
  mutation RenameAssistantConversation($conversationId: UUID!, $name: String!) {
    assistantConversationRename(conversationId: $conversationId, name: $name) {
      id
      name
    }
  }
`);

const TOGGLE_FAVORITE_MUTATION = gql(`
  mutation ToggleAssistantConversationFavorite($conversationId: UUID!) {
    assistantConversationToggleFavorite(conversationId: $conversationId) {
      id
      isFavorite
    }
  }
`);

const TOGGLE_SHARED_MUTATION = gql(`
  mutation ToggleAssistantConversationShared($conversationId: UUID!) {
    assistantConversationToggleShared(conversationId: $conversationId) {
      id
      isShared
    }
  }
`);

const GET_DOCUMENT_URL_MUTATION = gql(`
  mutation GetChatDocumentUrl($documentId: UUID!) {
    documentUrl(documentId: $documentId) {
      id
      filename
      presignedUrl
    }
  }
`);

const MESSAGE_FEEDBACK_MUTATION = gql(`
  mutation AssistantMessageFeedback($messageId: UUID!, $feedback: Int, $comment: String) {
    assistantMessageFeedback(messageId: $messageId, feedback: $feedback, comment: $comment) {
      id
      feedback
      feedbackComment
    }
  }
`);

/** The inverse of LIST_ROW_ACTIONS_REVEAL: a row's date fades out where its menu fades in. */
const META_HIDDEN_WHILE_ACTIONS =
  "group-hover/item:opacity-0 group-focus-within/item:opacity-0 group-has-aria-expanded/item:opacity-0 pointer-coarse:opacity-0";

/** For a customer chat transcript, the tenant it answered as. Undefined for the
 * service agents' chat. */
const CustomerChatContext = createContext<string | undefined>(undefined);

function ConversationsPane({
  onConversationChange,
  selectedConversationId,
}: {
  onConversationChange: (conversationId: string | undefined) => void;
  selectedConversationId?: string;
}) {
  return (
    <Pane>
      <PaneHeader
        title="Gespräche"
        actions={
          <IconButton
            icon={SquarePen}
            label="Neues Gespräch"
            size="sm"
            onClick={() => onConversationChange(undefined)}
          />
        }
      />
      <PaneBody padded={false} className="gap-0">
        <ErrorBoundary
          fallback={
            <EmptyState
              size="inline"
              variant="error"
              title="Fehler beim Laden der Gespräche"
              description="Bitte versuchen Sie es später erneut."
            />
          }
        >
          <Suspense
            fallback={
              <div role="status" className="flex flex-col gap-3 p-4">
                <span className="sr-only">Gespräche werden geladen</span>
                <Skeleton aria-hidden="true" shape="line" className="w-2/3" />
                <Skeleton aria-hidden="true" shape="line" />
                <Skeleton aria-hidden="true" shape="line" className="w-3/4" />
              </div>
            }
          >
            <ConversationsListContent
              onConversationChange={onConversationChange}
              selectedConversationId={selectedConversationId}
            />
          </Suspense>
        </ErrorBoundary>
      </PaneBody>
    </Pane>
  );
}

function ConversationsListContent({
  onConversationChange,
  selectedConversationId,
}: {
  onConversationChange: (conversationId: string | undefined) => void;
  selectedConversationId?: string;
}) {
  const { data } = useSuspenseQuery<AssistantConversationsQueryData>(
    ASSISTANT_CONVERSATIONS_QUERY,
  );
  const [renamingConversation, setRenamingConversation] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [newName, setNewName] = useState("");

  const [deleteConversation] = useMutation(DELETE_CONVERSATION_MUTATION, {
    refetchQueries: [ASSISTANT_CONVERSATIONS_QUERY],
    onCompleted: () => {
      toast.success("Gespräch gelöscht", {
        description: "Das Gespräch wurde erfolgreich gelöscht.",
      });
    },
    onError: () => {
      toast.error("Fehler beim Löschen", {
        description: "Das Gespräch konnte nicht gelöscht werden.",
      });
    },
  });

  const [renameConversation] = useMutation(RENAME_CONVERSATION_MUTATION, {
    optimisticResponse: (vars) => ({
      assistantConversationRename: {
        __typename: "AssistantConversation",
        id: vars.conversationId,
        name: vars.name,
      },
    }),
    onError: () => {
      toast.error("Fehler beim Umbenennen", {
        description: "Das Gespräch konnte nicht umbenannt werden.",
      });
    },
  });

  const [toggleFavorite] = useMutation(TOGGLE_FAVORITE_MUTATION, {
    optimisticResponse: (vars) => {
      // Find the conversation to get its current favorite status
      const conversation = data.assistantConversations.find(
        (c) => c.id === vars.conversationId,
      );
      return {
        assistantConversationToggleFavorite: {
          __typename: "AssistantConversation",
          id: vars.conversationId,
          isFavorite: !conversation?.isFavorite,
        },
      };
    },
    onError: () => {
      toast.error("Fehler", {
        description: "Der Favoriten-Status konnte nicht geändert werden.",
      });
    },
  });

  const [toggleShared] = useMutation(TOGGLE_SHARED_MUTATION, {
    onCompleted: (data) => {
      const isShared = (
        data as { assistantConversationToggleShared: { isShared: boolean } }
      ).assistantConversationToggleShared.isShared;
      toast.success(isShared ? "Gespräch geteilt" : "Teilen aufgehoben", {
        description: isShared
          ? "Das Gespräch ist jetzt für alle im Team sichtbar."
          : "Das Gespräch ist jetzt nur noch für Sie sichtbar.",
      });
    },
    onError: () => {
      toast.error("Fehler", {
        description: "Der Teilen-Status konnte nicht geändert werden.",
      });
    },
  });

  const handleDelete = (conversationId: string) => {
    void deleteConversation({
      variables: { conversationId },
    });
    // Clear selected conversation if it's the one being deleted
    if (selectedConversationId === conversationId) {
      onConversationChange(undefined);
    }
  };

  const handleRename = (conversation: AssistantConversationListItem) => {
    setRenamingConversation({
      id: conversation.id,
      name: conversation.name || "",
    });
    setNewName(conversation.name || "");
  };

  const handleRenameSubmit = () => {
    if (renamingConversation && newName.trim()) {
      // Close dialog immediately
      setRenamingConversation(null);
      const nameToSave = newName.trim();
      setNewName("");

      // Mutation happens in background with optimistic update
      void renameConversation({
        variables: {
          conversationId: renamingConversation.id,
          name: nameToSave,
        },
      });
    }
  };

  // Show empty state if no conversations
  if (data.assistantConversations.length === 0) {
    return (
      <EmptyState
        size="inline"
        icon={MessagesSquare}
        title="Keine vergangenen Gespräche"
        description="Starten Sie ein neues Gespräch."
      />
    );
  }

  // Separate favorites from regular conversations
  const favoriteConversations = data.assistantConversations.filter(
    (c) => c.isFavorite,
  );
  const regularConversations = data.assistantConversations.filter(
    (c) => !c.isFavorite,
  );

  const renderConversation = (conversation: AssistantConversationListItem) => {
    // Use conversation name or fallback to first message
    const displayMessages = conversation.messages.edges.filter(
      (edge) => !!edge.node.createdAt,
    );
    const displayName =
      conversation.name || displayMessages[0]?.node.text || "Neue Unterhaltung";

    const selected = selectedConversationId === conversation.id;
    return (
      <li
        key={conversation.id}
        data-selected={selected || undefined}
        className={cn(
          "group/item relative flex min-h-10 items-center gap-2 px-4 py-2 transition-colors",
          selected ? LIST_ROW_SELECTED : LIST_ROW_HOVER,
        )}
      >
        <button
          type="button"
          aria-current={selected ? "true" : undefined}
          onClick={() => onConversationChange(conversation.id)}
          className={cn(
            // The overlay makes the whole row the target; the ring is drawn on it.
            "min-w-0 flex-1 cursor-pointer truncate text-left font-sans text-body outline-hidden after:absolute after:inset-0",
            FOCUS_RING_INSET_AFTER,
            selected ? LIST_ROW_SELECTED_TEXT : "text-ink",
          )}
        >
          {displayName}
        </button>
        {/* The menu takes the date's place when it shows, so it reserves no column. */}
        <span
          className={cn(
            "flex shrink-0 items-center gap-1.5 font-sans text-caption text-ink-muted tabular-nums transition-opacity",
            META_HIDDEN_WHILE_ACTIONS,
          )}
        >
          {conversation.isShared && (
            <>
              <Icon icon={UsersIcon} size={12} />
              <span className="sr-only">Geteilt,</span>
            </>
          )}
          {formatDate(new Date(conversation.createdAt))}
        </span>
        <div
          className={cn(
            "absolute inset-y-0 right-2 z-10 flex items-center transition-opacity",
            LIST_ROW_ACTIONS_REVEAL,
          )}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton icon={MoreVerticalIcon} label="Mehr Optionen" size="sm" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" collisionPadding={8}>
              <DropdownMenuItem icon={Edit3Icon} onSelect={() => handleRename(conversation)}>
                Umbenennen
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={StarIcon}
                onSelect={() => void toggleFavorite({ variables: { conversationId: conversation.id } })}
              >
                {conversation.isFavorite ? "Aus Favoriten entfernen" : "Zu Favoriten"}
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={UsersIcon}
                onSelect={() => void toggleShared({ variables: { conversationId: conversation.id } })}
              >
                {conversation.isShared ? "Teilen aufheben" : "Teilen"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                icon={TrashIcon}
                variant="destructive"
                onSelect={() => handleDelete(conversation.id)}
              >
                Löschen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </li>
    );
  };

  const sectionHeading = "px-4 pt-4 pb-1 font-sans text-caption font-semibold text-ink-muted";

  return (
    <>
      {favoriteConversations.length > 0 && (
        <>
          <h3 className={sectionHeading}>Favoriten</h3>
          <List divided={false}>{favoriteConversations.map(renderConversation)}</List>
        </>
      )}

      {regularConversations.length > 0 && (
        <>
          {favoriteConversations.length > 0 && (
            <h3 className={sectionHeading}>Alle Gespräche</h3>
          )}
          <List divided={false}>{regularConversations.map(renderConversation)}</List>
        </>
      )}

      <Dialog
        open={renamingConversation !== null}
        onOpenChange={(open) => {
          if (!open) setRenamingConversation(null);
        }}
      >
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Gespräch umbenennen</DialogTitle>
            <DialogDescription>
              Geben Sie einen neuen Namen für dieses Gespräch ein.
            </DialogDescription>
          </DialogHeader>
          <form
            className="contents"
            onSubmit={(event) => {
              event.preventDefault();
              handleRenameSubmit();
            }}
          >
            <DialogBody>
              <Field label="Name">
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Gesprächsname"
                />
              </Field>
            </DialogBody>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Abbrechen</Button>
              </DialogClose>
              <Button type="submit" disabled={!newName.trim()}>
                Speichern
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** The reading column inside the chat pane: about 70 characters a line. */
const READING_COLUMN = "mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col";

function Welcome() {
  return (
    <EmptyState
      icon={MessagesSquare}
      title="Stellen Sie mir eine Frage"
      description="Erhalten Sie Antworten zu allen Service-Fragen."
    />
  );
}

function StartConversation({
  onConversationCreate,
  researchMode = false,
  onResearchModeChange,
  showResearchToggle = true,
}: {
  onConversationCreate: (conversationId: string) => void;
  researchMode?: boolean;
  onResearchModeChange?: (enabled: boolean) => void;
  showResearchToggle?: boolean;
}) {
  const [isCreating, setIsCreating] = useState(false);
  const [pendingMessage, setPendingMessage] = useState("");
  const [restore, setRestore] = useState<{ text: string } | undefined>();
  const pendingMessageRef = useRef("");
  // Stamped on send, so the optimistic message keeps its time across renders.
  const pendingAtRef = useRef("");
  const [createConversation] =
    useMutation<AssistantConversationStartMutationData>(
      CREATE_CONVERSATION_MUTATION,
      {
        onCompleted: (data) => {
          onConversationCreate(data.assistantConversationStart.id);
          setIsCreating(false);
        },
        onError: () => {
          setIsCreating(false);
          // The composer clears optimistically on submit, so restore the typed
          // message and tell the operator the start failed instead of silently
          // dropping their text back to the welcome screen.
          toast.error("Gespräch konnte nicht gestartet werden", {
            description: "Bitte versuchen Sie es erneut.",
          });
          setRestore({ text: pendingMessageRef.current });
        },
        update: (cache, { data }) => {
          if (!data?.assistantConversationStart) return;

          // Read existing conversations from cache
          const existingData = cache.readQuery<AssistantConversationsQueryData>({
            query: ASSISTANT_CONVERSATIONS_QUERY,
          });

          if (existingData?.assistantConversations) {
            const text = pendingMessageRef.current;
            // Create name from first message (truncated to 50 chars)
            const name =
              text.length <= 50 ? text : text.substring(0, 47) + "...";

            // Create the new conversation object with the initial message
            const newConversation: AssistantConversationListItem = {
              id: data.assistantConversationStart.id,
              createdAt: new Date().toISOString(),
              name,
              isFavorite: false,
              isShared: false,
              userId: "current-user", // Will be replaced by actual data from server
              messages: {
                edges: [
                  {
                    node: {
                      id: `temp-${data.assistantConversationStart.id}`,
                      text,
                      createdAt: new Date().toISOString(),
                    },
                  },
                ],
              },
            };

            // Write updated conversations list to cache
            cache.writeQuery<AssistantConversationsQueryData>({
              query: ASSISTANT_CONVERSATIONS_QUERY,
              data: {
                assistantConversations: [
                  newConversation,
                  ...existingData.assistantConversations,
                ],
              },
            });
          }
        },
      },
    );

  const handleSubmit = (text: string, attachmentIds?: string[]) => {
    pendingMessageRef.current = text;
    pendingAtRef.current = new Date().toISOString();
    setPendingMessage(text);
    setIsCreating(true);
    void createConversation({
      variables: {
        text,
        researchMode,
        attachmentIds,
      },
    });
  };

  return (
    <div className={READING_COLUMN}>
      {/* While the conversation is created, the sent message and a waiting answer
          show at once instead of after the create round-trip and the first fetch. */}
      <AssistantMessageFeed
        className="flex-1"
        displayMessages={
          isCreating
            ? [
                {
                  id: "temp-pending",
                  createdAt: pendingAtRef.current,
                  text: pendingMessage,
                  isFromUser: true,
                  agent: { id: "current-user" },
                },
              ]
            : []
        }
        isWaitingForResponse={isCreating}
        empty={<Welcome />}
      />
      <div className="shrink-0 px-4 pb-4">
        <AssistantChatInput
          onSubmit={handleSubmit}
          isSending={isCreating}
          showResearchToggle={showResearchToggle}
          researchMode={researchMode}
          onResearchModeChange={onResearchModeChange}
          restore={restore}
          allowAttachments
        />
      </div>
    </div>
  );
}

function MessagingInterface({
  conversationId,
  researchMode = false,
  onResearchModeChange,
  showResearchToggle = true,
  readOnly = false,
  feedback,
  answerFooter,
}: {
  conversationId: string;
  researchMode?: boolean;
  onResearchModeChange?: (enabled: boolean) => void;
  showResearchToggle?: boolean;
  /** A transcript to read: no composer, no feedback, no answer to a choice. */
  readOnly?: boolean;
  /** The reader's rating of each answer; the chat user's own when not given (none when read-only). */
  feedback?: (message: AssistantMessage) => ChatFeedback;
  /** Rendered below each answer, under its thumbs. */
  answerFooter?: (message: AssistantMessage) => ReactNode;
}) {
  const {
    messages,
    displayMessages,
    progressMessage,
    isWaitingForResponse,
    isSending,
    responseTimedOut,
    sendMessage,
    updateMessageFeedback,
  } = useAssistantMessages({ conversationId });

  const [selectedReferenceId, setSelectedReferenceId] = useState<string | null>(
    null,
  );
  // #581: which citation the pointer is over. Hovering a citation chip in the
  // message text highlights the matching row in that message's source list, so
  // the agent can see where a claim came from by pointing at it. Click is
  // unchanged and still opens the document -- a download cannot live on
  // mouseenter, or sweeping across a sentence would open several.
  const [hoveredReferenceId, setHoveredReferenceId] = useState<string | null>(
    null,
  );
  // Memoized so the Set identity is stable while the pointer rests on one chip:
  // MessageReferences takes a Set, and a fresh one per render would re-render
  // every source row on every commit.
  const highlightedReferenceIds = useMemo(
    () => (hoveredReferenceId ? new Set([hoveredReferenceId]) : null),
    [hoveredReferenceId],
  );

  const [getDocumentUrl] = useMutation<DocumentUrlMutationData>(
    GET_DOCUMENT_URL_MUTATION,
    {
      onError: () => {
        toast.error("Fehler beim Herunterladen", {
          description: "Das Dokument konnte nicht heruntergeladen werden.",
        });
      },
    },
  );

  const [submitFeedback] = useMutation(MESSAGE_FEEDBACK_MUTATION, {
    onError: () => {
      toast.error("Feedback konnte nicht gespeichert werden");
    },
  });

  const customerFacingAs = useContext(CustomerChatContext);

  // A customer chat transcript opens a cited document where the customer card does: at
  // its public original, where the tenant links one. The others keep our signed link.
  const citedDocumentIds = useMemo(
    () =>
      [
        ...new Set(
          messages.flatMap((m) =>
            (m.references ?? [])
              .filter((r) => r.type === ReferenceType.Document && r.sourceDocumentId)
              .map((r) => r.sourceDocumentId!),
          ),
        ),
      ].sort(),
    [messages],
  );
  const { data: linkData } = useQuery<DocumentLinksQueryData>(DOCUMENT_LINKS_QUERY, {
    variables: { documentIds: citedDocumentIds },
    skip: customerFacingAs === undefined || citedDocumentIds.length === 0,
  });
  const documentLinks = useMemo(
    () =>
      linkData
        ? new Map<string, PresignedUrlEntry>(
            linkData.documentLinks.map((link) => [
              link.documentId,
              { url: link.sourceUrl, expiresAt: NO_EXPIRY },
            ]),
          )
        : undefined,
    [linkData],
  );

  const [setResearchModeMutation] = useMutation(SET_RESEARCH_MODE_MUTATION, {
    onError: () => {
      toast.error("Recherche-Modus konnte nicht geändert werden");
    },
  });

  const handleResearchModeChange = (enabled: boolean) => {
    onResearchModeChange?.(enabled);
    void setResearchModeMutation({ variables: { conversationId, enabled } });
  };

  // Show the research mode while the agent auto-escalates this turn (a
  // deep_research tool call is RUNNING), even if the operator did not pick it.
  const researchActive = messages.some((m) =>
    [...(m.toolCalls ?? []), ...(m.toolCallsAfterText ?? [])].some(
      (tc) => tc.toolName === "deep_research" && tc.status === "RUNNING",
    ),
  );

  const handleFeedback = (messageId: string, rating: ChatRating, comment: string) => {
    updateMessageFeedback(messageId, rating, comment);
    void submitFeedback({
      variables: { messageId, feedback: rating, comment },
    });
  };

  const handleDownloadDocument = (
    documentId: string | null | undefined,
    pageNumber?: number | null,
  ) => {
    if (!documentId) {
      toast.error("Dokument nicht gefunden", {
        description: "Das Dokument konnte nicht heruntergeladen werden.",
      });
      return;
    }

    void getDocumentUrl({
      variables: { documentId },
      onCompleted: (data) => {
        const url = `${data.documentUrl.presignedUrl}${pageNumber ? "#page=" + (pageNumber + 1) : ""}`;
        window.open(url, "_blank");
      },
    });
  };

  const handleReferenceClick = (
    referenceId: string,
    reference: AssistantReference,
  ) => {
    setSelectedReferenceId(referenceId);
    if (reference.sourceDocumentId) {
      handleDownloadDocument(
        reference.sourceDocumentId,
        reference.sourceDocumentPageNumber,
      );
    }
  };

  /** Bridge for rehype plugin which uses (messageId, referenceId) signature */
  const handleRehypeReferenceClick = (
    _messageId: string,
    referenceId: string,
    references: AssistantReference[] | undefined,
  ) => {
    const ref = references?.find((r) => r.id === referenceId);
    if (ref) handleReferenceClick(referenceId, ref);
  };

  return (
    <div className={READING_COLUMN}>
      <AssistantMessageFeed
        className="flex-1"
        displayMessages={displayMessages}
        assistantName={customerFacingAs}
        progressMessage={progressMessage}
        isWaitingForResponse={isWaitingForResponse}
        responseTimedOut={responseTimedOut}
        feedback={feedback ?? (readOnly ? undefined : (message) => ownFeedback(message, handleFeedback))}
        answerFooter={answerFooter}
        references={{
          onReferenceClick: handleReferenceClick,
          selectedReferenceId,
          highlightedReferenceIds,
          presignedUrls: documentLinks,
        }}
        onChoiceSelect={readOnly ? undefined : (option) => sendMessage(option)}
        buildRehypePlugins={(messageId, references) => [
          [
            rehypeLinkifySources,
            messageId,
            (_messageId: string, referenceId: string) =>
              handleRehypeReferenceClick(_messageId, referenceId, references),
            { hoverCallback: setHoveredReferenceId },
            references?.filter((r) => r.index != null),
          ],
        ]}
      />

      {!readOnly && (
        <div className="shrink-0 px-4 pb-4">
          <AssistantChatInput
            onSubmit={sendMessage}
            isSending={isSending}
            generating={isWaitingForResponse}
            showResearchToggle={showResearchToggle}
            researchMode={researchMode}
            onResearchModeChange={handleResearchModeChange}
            researchActive={researchActive}
            allowAttachments
            conversationId={conversationId}
          />
        </div>
      )}
    </div>
  );
}

function ConversationError({ onBack }: { onBack?: () => void }) {
  return (
    <div className="flex flex-1 items-center justify-center p-4">
      <EmptyState
        variant="error"
        icon={CircleAlert}
        title="Fehler beim Laden des Gesprächs"
        description="Das Gespräch konnte nicht geladen werden."
        actions={
          onBack && (
            <Button variant="outline" onClick={onBack}>
              Zurück zur Übersicht
            </Button>
          )
        }
      />
    </div>
  );
}

/** One customer chat as its customer saw it, for staff to read through: answered in
 * the tenant's name, with its tool calls and sources, and nothing to type into. The
 * reader rates each answer and says why on its thumbs; below them stand the customer's
 * own rating and the other reviewers'. A feedback report sent while the transcript is
 * open lands on this chat's session. */
export function ChatTranscript({
  conversationId,
  tenantName,
}: {
  conversationId: string;
  tenantName: string;
}) {
  useEffect(() => {
    setLytraSentryContext({ chatConversationId: conversationId });
    return () => setLytraSentryContext({ chatConversationId: null });
  }, [conversationId]);
  const reviews = useAnswerReviews(conversationId);

  return (
    <CustomerChatContext.Provider value={tenantName}>
      <ErrorBoundary fallback={<ConversationError />}>
        <MessagingInterface
          conversationId={conversationId}
          readOnly
          feedback={(message) => {
            const own = reviews.reviewsOf(message.id).find((r) => r.isOwn);
            return {
              value: chatRating(own?.rating),
              comment: own?.comment,
              onSave: (rating, comment) => reviews.save(message.id, rating, comment),
            };
          }}
          answerFooter={(message) => (
            <AnswerReviews
              reviews={reviews.reviewsOf(message.id).filter((r) => !r.isOwn)}
              customerFeedback={rated(message.feedback, message.feedbackComment)}
            />
          )}
        />
      </ErrorBoundary>
    </CustomerChatContext.Provider>
  );
}

/**
 * The service agents' chat: the conversation list and the chat side by side, as
 * two panes of the host's surface. In a narrow surface (under 40rem) one pane
 * shows at a time; the chat header's list button swaps them.
 */
export default function Chat({
  conversationId,
  onConversationChange,
  showResearchToggle = true,
}: {
  conversationId?: string;
  onConversationChange: (conversationId: string | undefined) => void;
  /** Whether the chat that answers has a research mode to offer. */
  showResearchToggle?: boolean;
}) {
  // Deep-research toggle, held at the Chat level so it carries from the
  // start-screen across the create -> message transition within a session.
  const [researchMode, setResearchMode] = useState(false);
  const [listOpen, setListOpen] = useState(false);

  const selectConversation = (id: string | undefined) => {
    setListOpen(false);
    onConversationChange(id);
  };

  return (
    <div className="@container flex min-h-0 min-w-0 flex-1">
      <div
        className={cn(
          "min-h-0 w-full shrink-0 @min-[40rem]:flex @min-[40rem]:w-80 @min-[40rem]:border-r @min-[40rem]:border-divider",
          listOpen ? "flex" : "hidden",
        )}
      >
        <ConversationsPane
          onConversationChange={selectConversation}
          selectedConversationId={conversationId}
        />
      </div>
      <div className={cn("min-h-0 min-w-0 flex-1 @min-[40rem]:flex", listOpen ? "hidden" : "flex")}>
        <Pane>
          <PaneHeader
            title="Assistent"
            icon={AiMarker}
            actions={
              <>
                <IconButton
                  icon={PanelLeft}
                  label="Gespräche anzeigen"
                  size="sm"
                  className="@min-[40rem]:hidden"
                  onClick={() => setListOpen(true)}
                />
                <IconButton
                  icon={SquarePen}
                  label="Neues Gespräch"
                  size="sm"
                  className="@min-[40rem]:hidden"
                  onClick={() => selectConversation(undefined)}
                />
              </>
            }
          />
          {conversationId ? (
            <ErrorBoundary
              key={conversationId}
              fallback={<ConversationError onBack={() => selectConversation(undefined)} />}
            >
              <Suspense
                fallback={
                  <div className="flex flex-1 items-center justify-center">
                    <Throbber size={32} label="Gespräch wird geladen" />
                  </div>
                }
              >
                <MessagingInterface
                  conversationId={conversationId}
                  researchMode={researchMode}
                  onResearchModeChange={setResearchMode}
                  showResearchToggle={showResearchToggle}
                />
              </Suspense>
            </ErrorBoundary>
          ) : (
            <StartConversation
              onConversationCreate={onConversationChange}
              researchMode={researchMode}
              onResearchModeChange={setResearchMode}
              showResearchToggle={showResearchToggle}
            />
          )}
        </Pane>
      </div>
    </div>
  );
}
